import { MatjrahClient as Client } from "./client";

const normalizeBaseURL = (value) => {
  const fallback = process.env.BASE_URI_API || "https://api.tajadad.app/api/";
  const baseURL = String(value || fallback).trim();

  return `${baseURL.replace(/\/+$/, "")}/`;
};

const runtimeBaseURL = () =>
  typeof window !== "undefined"
  && window.__TAJADAD_OC_CONFIG__
  && window.__TAJADAD_OC_CONFIG__.apiBase;

const canRenewEditorSession = () =>
  typeof window !== "undefined"
  && typeof window.__RENEW_TAJADAD_EDITOR_SESSION__ === "function";

const renewEditorSessionAndRetry = async (
  resourceURL,
  method,
  requestBodyOrQuery,
  requestHeaders
) => {
  await window.__RENEW_TAJADAD_EDITOR_SESSION__();
  return executeRequest(
    resourceURL,
    method,
    requestBodyOrQuery,
    requestHeaders
  );
};

const executeRequest = async (
  resourceURL,
  method,
  requestBodyOrQuery,
  requestHeaders
) => {
  const requestURL = new URL(
    resourceURL,
    normalizeBaseURL(runtimeBaseURL())
  );

  if (requestBodyOrQuery && method.toUpperCase() === "GET") {
    Object.keys(requestBodyOrQuery).forEach((key) => {
      const value = requestBodyOrQuery[key];
      if (value !== undefined && value !== null && value !== "") {
        requestURL.searchParams.set(key, value);
      }
    });
  }

  const token = localStorage.getItem("access_token");
  const headers = {
    ...Client.defaultHeaders,
    ...requestHeaders,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const requestDetails = { method, headers };

  if (requestBodyOrQuery && method.toUpperCase() !== "GET") {
    requestDetails.body = new URLSearchParams(requestBodyOrQuery);
  }

  return fetch(requestURL.toString(), requestDetails);
};

export const request = async (
  resourceURL,
  method = "GET",
  requestBodyOrQuery = null,
  requestHeaders = {}
) => {
  let response;
  try {
    response = await executeRequest(
      resourceURL,
      method,
      requestBodyOrQuery,
      requestHeaders
    );
  } catch (error) {
    if (!canRenewEditorSession()) {
      throw Error("تعذر الاتصال ببيانات التطبيق. أعد تحميل الصفحة وحاول مرة أخرى.");
    }

    try {
      response = await renewEditorSessionAndRetry(
        resourceURL,
        method,
        requestBodyOrQuery,
        requestHeaders
      );
    } catch (retryError) {
      throw Error("تعذر الاتصال ببيانات التطبيق. أعد تحميل الصفحة وحاول مرة أخرى.");
    }
  }

  // Embedded OC editor sessions are intentionally short lived. A tab can
  // resume just after its token expires, before the focus renewal completes.
  // Renew once on an authentication response and replay the original request.
  // Authentication middleware rejects the first request before any mutation,
  // so this is safe for both reads and writes without duplicate side effects.
  if (response.status === 401 && canRenewEditorSession()) {
    try {
      response = await renewEditorSessionAndRetry(
        resourceURL,
        method,
        requestBodyOrQuery,
        requestHeaders
      );
    } catch (retryError) {
      throw Error("انتهت جلسة محرر التطبيق. أعد تحميل الصفحة وحاول مرة أخرى.");
    }
  }

  const responseText = await response.text();

  if (response.ok) {
    return JsonParse(responseText).data;
  }

  // Handle error
  Client.onResponseStatus(response.status, responseText);
  throw Error(normalizeErrorMessage(JsonParse(responseText).message));
};

// === Helpers ===
const normalizeErrorMessage = (message) => {
  if (typeof message === "string" && message.trim()) {
    return message;
  }

  if (Array.isArray(message)) {
    const firstMessage = message
      .flat(Infinity)
      .find((item) => typeof item === "string" && item.trim());
    if (firstMessage) return firstMessage;
  }

  if (message && typeof message === "object") {
    const preferredMessage = message.message || message.Message || message.error;
    if (typeof preferredMessage === "string" && preferredMessage.trim()) {
      return preferredMessage;
    }
  }

  return "تعذر تحميل بيانات المتجر الآن. أعد المحاولة بعد لحظات.";
};

const JsonParse = (string) => {
  try {
    return JSON.parse(string);
  } catch {
    throw Error("Unexpected error occurred");
  }
};
